/**
 * useConversationCommands — pure state hook for slash-command mode in the
 * conversation composer (Spec 91 / command-aware composer).
 *
 * Pure helpers (detectSlashCommand, buildCommandContext, buildReplyBody) are
 * exported so tests can exercise them without rendering.
 */
import { useState, useCallback } from 'react';
import type { ConversationSurface } from '@confluent/shared/lib/conversation-surface';
import type { ConversationReplyRequest, FinanceCommandLookupOption } from '../generated/api/types.gen.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CommandName = 'indicator' | 'strategy' | 'ticker' | 'research' | 'timeframe';

/** A single selected command option (one entry in the chip strip). */
export interface CommandSelection {
  type: FinanceCommandLookupOption['type'];
  id: string;
  label: string;
  source: string;
  installed: boolean;
  metadata: FinanceCommandLookupOption['metadata'];
}

/** Key for the selections Map: `${type}:${id}`. */
export type SelectionKey = string;

// Alias for the generated commandContext field, without `any`.
export type ConversationCommandContext = NonNullable<ConversationReplyRequest['commandContext']>;

// ─── Pure helpers (unit-testable) ─────────────────────────────────────────────

const COMMAND_RE = /^\/(indicator|strategy|ticker|research|timeframe)(\s|$)/i;

/**
 * Detect a slash command at position 0 ONLY.
 * Returns the matched command + the text after the command token, or null.
 */
export function detectSlashCommand(text: string): { command: CommandName; rest: string } | null {
  const match = COMMAND_RE.exec(text);
  if (!match) return null;
  const command = match[1].toLowerCase() as CommandName;
  const rest = text.slice(match[0].length);
  return { command, rest };
}

const MAX_REFS = 24;

/**
 * Build the commandContext object from current selections + research objective.
 * Returns null when there is nothing to attach.
 */
export function buildCommandContext(
  selections: Map<SelectionKey, CommandSelection>,
  researchObjective: string | null,
  researchModeId: string | null,
): ConversationCommandContext | null {
  const hasSelections = selections.size > 0;
  const hasObjective = !!researchObjective;
  if (!hasSelections && !hasObjective) return null;

  const allEntries = Array.from(selections.values());
  const truncated = allEntries.length > MAX_REFS;
  const refs = allEntries.slice(0, MAX_REFS).map((s) => {
    // Only carry primitive values in metadata to conform to generated type.
    const safeMeta: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(s.metadata)) {
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        safeMeta[k] = v;
      }
    }
    return {
      type: s.type,
      id: s.id,
      label: s.label,
      moduleId: 'finance' as const,
      ...(Object.keys(safeMeta).length > 0 ? { metadata: safeMeta } : {}),
    };
  });

  if (truncated) {
    console.warn('[useConversationCommands] Selection count exceeded 24; truncating to 24 references.');
  }

  // Derive persona slug from research mode (stored in metadata.personaSlug).
  const researchEntry = researchModeId
    ? allEntries.find((s) => s.type === 'research' && s.id === researchModeId)
    : null;
  const preferredPersonaSlug =
    researchEntry && typeof researchEntry.metadata['personaSlug'] === 'string'
      ? researchEntry.metadata['personaSlug']
      : researchObjective
        ? 'finance.asset-research-analyst'
        : undefined;

  const ctx: ConversationCommandContext = {
    version: 1,
    source: 'conversation_composer',
    ...(refs.length > 0 ? { references: refs } : {}),
    ...(researchObjective ? { researchPrompt: researchObjective } : {}),
    routingHints: {
      moduleId: 'finance',
      ...(preferredPersonaSlug ? { preferredPersonaSlug } : {}),
    },
  };

  return ctx;
}

/**
 * Build the full reply body. Exported as a pure seam for testing send payloads.
 * When commandContext is null, the key is omitted entirely (no undefined spread).
 */
export function buildReplyBody(
  content: string,
  commandContext: ConversationCommandContext | null,
  surface?: ConversationSurface,
  /** What the page shows right now — an assistant's form — sent so the turn sees it as it is, not as it was. */
  contextSpec?: Record<string, unknown>,
  /** Who the message is for (member addresses), the delivery it answers — conversation-members spec — and its files. */
  addressing?: Pick<ConversationReplyRequest, 'to' | 'answers' | 'attachments'>,
): ConversationReplyRequest {
  const base: ConversationReplyRequest = {
    role: 'user',
    content,
    ...(surface ? { surface } : {}),
    ...(contextSpec ? { contextSpec } : {}),
    ...(addressing?.to && addressing.to.length > 0 ? { to: addressing.to } : {}),
    ...(addressing?.answers !== undefined ? { answers: addressing.answers } : {}),
    ...(addressing?.attachments?.length ? { attachments: addressing.attachments } : {}),
  };
  if (commandContext) {
    return { ...base, commandContext };
  }
  return base;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface ConversationCommandsState {
  activeCommand: CommandName | null;
  searchQuery: string;
  selections: Map<SelectionKey, CommandSelection>;
  researchObjective: string | null;
  researchModeId: string | null;
}

export interface ConversationCommandsActions {
  enterCommand: (command: CommandName, initialQuery?: string) => void;
  updateSearch: (q: string) => void;
  toggleSelection: (option: FinanceCommandLookupOption) => void;
  removeSelection: (key: SelectionKey) => void;
  clearCommand: () => void;
  clearAll: () => void;
  captureResearchObjective: (text: string) => void;
  removeResearchObjective: () => void;
}

const INITIAL_STATE: ConversationCommandsState = {
  activeCommand: null,
  searchQuery: '',
  selections: new Map(),
  researchObjective: null,
  researchModeId: null,
};

export function useConversationCommands(): ConversationCommandsState & ConversationCommandsActions {
  const [state, setState] = useState<ConversationCommandsState>(INITIAL_STATE);

  const enterCommand = useCallback((command: CommandName, initialQuery = '') => {
    setState((prev) => ({
      ...prev,
      activeCommand: command,
      searchQuery: initialQuery,
    }));
  }, []);

  const updateSearch = useCallback((q: string) => {
    setState((prev) => ({ ...prev, searchQuery: q }));
  }, []);

  const toggleSelection = useCallback((option: FinanceCommandLookupOption) => {
    const key: SelectionKey = `${option.type}:${option.id}`;
    setState((prev) => {
      const next = new Map(prev.selections);
      let researchModeId = prev.researchModeId;
      if (next.has(key)) {
        next.delete(key);
        if (option.type === 'research') researchModeId = null;
      } else {
        if (option.type === 'research') {
          for (const existingKey of next.keys()) {
            if (existingKey.startsWith('research:')) next.delete(existingKey);
          }
          researchModeId = option.id;
        }
        next.set(key, {
          type: option.type,
          id: option.id,
          label: option.label,
          source: option.source,
          installed: option.installed,
          metadata: option.metadata,
        });
      }
      return { ...prev, selections: next, researchModeId };
    });
  }, []);

  const removeSelection = useCallback((key: SelectionKey) => {
    setState((prev) => {
      const next = new Map(prev.selections);
      next.delete(key);
      return {
        ...prev,
        selections: next,
        researchModeId: key === `research:${prev.researchModeId}` ? null : prev.researchModeId,
      };
    });
  }, []);

  const clearCommand = useCallback(() => {
    // Exit mode but keep selections.
    setState((prev) => ({ ...prev, activeCommand: null, searchQuery: '' }));
  }, []);

  const clearAll = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const captureResearchObjective = useCallback((text: string) => {
    setState((prev) => ({ ...prev, researchObjective: text }));
  }, []);

  const removeResearchObjective = useCallback(() => {
    setState((prev) => ({ ...prev, researchObjective: null }));
  }, []);

  return {
    ...state,
    enterCommand,
    updateSearch,
    toggleSelection,
    removeSelection,
    clearCommand,
    clearAll,
    captureResearchObjective,
    removeResearchObjective,
  };
}
